import {
  AlertCircle,
  Check,
  CircleDashed,
  Clock,
  FileImage,
  FileText,
  Mail,
  Receipt,
  SquareArrowOutUpRight,
  UserCheck,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { MessageNode } from "./utils";
import { isToolCallMessage } from "./utils";
import {
  getRichActionDisplayName,
  getRichActionSubtitle,
  type RichActionType,
} from "@/modules/agents/components/helpers/richActionHelpers";
import { TimelineStepShell } from "./TimelineStepShell";

const ACTION_ICON_MAP: Record<RichActionType, LucideIcon> = {
  validate_quote_action: FileText,
  finalize_quote_action: FileText,
  validate_invoice_action: Receipt,
  validate_design_files_action: FileImage,
  user_review_action: UserCheck,
  send_email_action: Mail,
  email_send_action: Mail,
  gmail_send_mail_forgepoint: Mail,
  smtp_send_email_forgepoint: Mail,
  microsoft_outlook_send_mail_forgepoint: Mail,
};

interface TimelineRichActionStepProps {
  node: MessageNode;
  richActionType: RichActionType;
  error: string | null;
  onOpen?: () => void;
  isThreadActive: boolean;
}

/**
 * A rich action (email send, quote finalize, etc.) as a timeline step.
 * Clicking the row opens the action's preview in the side panel when the user
 * is allowed to. Top-level pending-review actions render outside the timeline,
 * but sub-agent ones can still surface here in a pending state.
 */
export function TimelineRichActionStep({
  node,
  richActionType,
  error,
  onOpen,
  isThreadActive,
}: TimelineRichActionStepProps) {
  const msg = node.message.message;
  const args = isToolCallMessage(msg) ? msg.arguments : {};
  const subtitle = getRichActionSubtitle(richActionType, args);
  const isCancelled = node.message.task?.status === "CANCELLED";
  const status = resolveStatus(node, error);
  const StatusIcon = status.icon;

  return (
    <TimelineStepShell
      icon={ACTION_ICON_MAP[richActionType]}
      label={
        <span className={cn(isCancelled && "opacity-60")}>
          <span className="font-medium">
            {getRichActionDisplayName(richActionType)}
          </span>
          {subtitle && (
            <span className="text-muted-foreground"> · {subtitle}</span>
          )}
        </span>
      }
      meta={
        <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
          <StatusIcon className={cn("h-3 w-3", status.iconClassName)} />
          {status.statusLabel}
          {onOpen && <SquareArrowOutUpRight className="h-3 w-3" />}
        </span>
      }
      isInProgress={isThreadActive && !node.toolResult && !node.message.task}
      onClick={onOpen}
    />
  );
}

function resolveStatus(node: MessageNode, error: string | null) {
  if (error) {
    return {
      icon: AlertCircle,
      iconClassName: "text-danger",
      statusLabel: "Failed",
    };
  }
  const taskStatus = node.message.task?.status;
  if (taskStatus === "CANCELLED") {
    return { icon: X, iconClassName: "", statusLabel: "Cancelled" };
  }
  if (taskStatus === "PENDING_REVIEW") {
    return {
      icon: Clock,
      iconClassName: "text-warning",
      statusLabel: "Pending review",
    };
  }
  // No result and no approval: the thread stopped before the action executed.
  if (!node.toolResult && taskStatus !== "APPROVED") {
    return {
      icon: CircleDashed,
      iconClassName: "",
      statusLabel: "Incomplete",
    };
  }
  return {
    icon: Check,
    iconClassName: "text-success",
    statusLabel: "Completed",
  };
}
